// ---------------------------------------------------------------------------
// ConversationView.tsx
//
// Two load paths:
//
// FRESH navigation (from AskAboutButton / SnapshotFeed "Discuss" button):
//   location.state.contextRef is present.
//   contextRefToSystemTurn converts it to a system turn:
//     - synchronously for event / asset / alert / signal / news
//     - async (one fetch) for snapshot — the API returns a ConversationTurn directly
//
// SAVED conversation (from ConversationListView):
//   No location.state. Load turns[] from Firestore by conversationId.
//   Turns already contain ConversationBlock[] — render directly, no transform.
//   useConversation hook owns the Firestore subscription.
//
// DATA FLOW PER SUBMIT
// --------------------
//  1. Capture userTurnIndex = conversation.turns.length (before any mutation)
//  2. Optimistic user turn appended to local state immediately
//  3. If first submit on fresh path: createConversation()
//  4. saveUserTurn() — writes to Firestore with the pre-captured index
//  5. useAISearch.submit() — POST /api/chat (sends full history[])
//  6. Optimistic assistant turn appended
//  7. saveAssistantTurn() — writes to Firestore
//  8. If title returned: updateTitle()
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAISearch } from '../api/useAISearch';
import { useConversation } from '../api/useConversation';
import { ConversationThread } from '../components/ConversationThread';
import { AISearchField } from '../components/AISearchField';
import type { ContextRef } from '../api/contextTypes';
import { contextRefToSystemTurn } from '../api/contextTypes';
import type { ConversationTurn } from '../api/aiSearchTypes';
import { FULL_SCREEN_INSETS } from '../../layout/chrome';

interface LocationState {
    initialQuery?: string;
    contextRef?: ContextRef;
}

export function ConversationView() {
    const { conversationId } = useParams<{ conversationId: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    const bottomRef = useRef<HTMLDivElement>(null);
    const hasSubmittedInitial = useRef(false);

    // Snapshot navigation state once on mount. Without this, the browser
    // restores location.state on Back, replaying the fresh-nav flow against
    // an already-created conversation and tripping the rules.
    const [{ initialQuery, contextRef }] = useState<LocationState>(
        () => (location.state as LocationState) ?? {}
    );

    useEffect(() => {
        if (location.state) {
            navigate(location.pathname, { replace: true, state: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // isFreshNav: user just navigated here with state (AskAboutButton / Discover search)
    // vs. returning to a saved conversation by URL (no location.state)
    const isFreshNav = Boolean(contextRef || initialQuery);

    // -----------------------------------------------------------------------
    // Hooks
    // -----------------------------------------------------------------------

    const aiSearch = useAISearch();
    const conversation = useConversation(conversationId!, isFreshNav);

    // -----------------------------------------------------------------------
    // Opening system turn (fresh path only)
    // -----------------------------------------------------------------------

    const [systemTurn, setSystemTurn] = useState<ConversationTurn | null>(null);
    const [systemTurnLoading, setSystemTurnLoading] = useState(false);

    useEffect(() => {
        if (!contextRef) return;

        if (contextRef.type === 'snapshot') {
            // Async path: snapshot turn is fetched from the API
            setSystemTurnLoading(true);
            contextRefToSystemTurn(contextRef)
                .then(setSystemTurn)
                .catch(err =>
                    console.error('Failed to load snapshot turn:', err)
                )
                .finally(() => setSystemTurnLoading(false));
        } else {
            // Sync path: all other types convert instantly
            setSystemTurn(contextRefToSystemTurn(contextRef));
        }
        // contextRef is stable — it comes from location.state which never changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // -----------------------------------------------------------------------
    // Full turn list for rendering
    //
    // Fresh path:  [systemTurn?, ...conversation.turns]
    // Saved path:  [...conversation.turns]  (system turn is already in Firestore)
    // -----------------------------------------------------------------------

    const allTurns: ConversationTurn[] = [
        ...(isFreshNav && systemTurn ? [systemTurn] : []),
        ...conversation.turns,
    ];

    // -----------------------------------------------------------------------
    // Submit handler
    // -----------------------------------------------------------------------

    const handleSubmit = useCallback(
        async (text: string) => {
            if (!text.trim() || !conversationId) return;
            // Don't let a submit fire while the SAVED path is still loading —
            // turns=[] + meta=null would trip isFirstSubmit and clobber the doc.
            if (conversation.loading) return;

            const isFirstSubmit =
                conversation.turns.length === 0 &&
                !conversation.conversationMeta;

            // Capture the index before any optimistic mutation so it can't
            // be skewed by the pending setTurns from appendOptimisticTurn.
            const userTurnIndex = conversation.turns.length;

            // 1. Optimistic user turn
            const optimisticUserTurn: ConversationTurn = {
                role: 'user',
                blocks: [{ type: 'text', content: text }],
                text,
                mentionRefs: [],
            };
            conversation.appendOptimisticTurn(optimisticUserTurn);

            try {
                // 2. Create Firestore doc on first submit when no meta exists.
                // Gating on meta (not isFreshNav) covers the "New chat" entry
                // from ConversationListView, which navigates with empty state.
                if (isFirstSubmit) {
                    await conversation.createConversation(
                        conversationId,
                        text,
                        contextRef ?? null
                    );
                }

                // 3. Write user turn to Firestore
                await conversation.saveUserTurn(
                    conversationId,
                    text,
                    [], // mentionRefs — populated in Phase 3
                    optimisticUserTurn.blocks,
                    userTurnIndex
                );

                // 4. Call the API — sends full history[] for LLM context
                const result = await aiSearch.submit({
                    conversationId,
                    query: text,
                    turns: allTurns,
                    conversationContextRef: isFirstSubmit
                        ? contextRef ?? null
                        : undefined,
                    mentionRefs: [],
                });

                // 5. Optimistic assistant turn
                const optimisticAssistantTurn: ConversationTurn = {
                    role: 'assistant',
                    blocks: result.blocks,
                    text: result.blocks
                        .filter(
                            (b): b is { type: 'text'; content: string } =>
                                b.type === 'text'
                        )
                        .map(b => b.content)
                        .join('\n\n'),
                    contextRefs: [],
                };
                conversation.appendOptimisticTurn(optimisticAssistantTurn);

                // 6. Write assistant turn to Firestore
                await conversation.saveAssistantTurn(
                    conversationId,
                    userTurnIndex,
                    optimisticAssistantTurn
                );

                // 7. Update title if returned (first turn only)
                if (result.title) {
                    await conversation.updateTitle(
                        conversationId,
                        result.title
                    );
                }
            } catch (err) {
                conversation.appendOptimisticTurn({
                    role: 'assistant',
                    blocks: [
                        {
                            type: 'alert_banner',
                            severity: 'error',
                            title: 'Response failed',
                            body:
                                err instanceof Error
                                    ? err.message
                                    : 'Something went wrong. Please try again.',
                        },
                    ],
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            conversationId,
            conversation,
            aiSearch,
            contextRef,
            isFreshNav,
            allTurns,
        ]
    );

    // -----------------------------------------------------------------------
    // Auto-submit initial query from navigation state
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (
            initialQuery &&
            !hasSubmittedInitial.current &&
            !systemTurnLoading
        ) {
            hasSubmittedInitial.current = true;
            handleSubmit(initialQuery);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialQuery, systemTurnLoading]);

    // -----------------------------------------------------------------------
    // Scroll to bottom on new turns
    // -----------------------------------------------------------------------

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [allTurns.length]);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    // Block submit until the SAVED-path onSnapshot has resolved. Otherwise
    // a fast follow-up could see turns=[] and meta=null and re-trigger
    // createConversation, overwriting the existing doc with setDoc.
    const isLoading = aiSearch.loading || conversation.loading;

    return (
        <Box
            sx={{
                position: 'fixed',
                // Between the bars: the phone bars below md, the desktop
                // header from md on (see FULL_SCREEN_INSETS).
                ...FULL_SCREEN_INSETS,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                zIndex: t => t.zIndex.appBar - 1,
            }}
        >
            {/* Header — no horizontal/vertical padding on the container so bgcolor fills edge-to-edge */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pl: 1,
                    pr: 2,
                    py: 1.25,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                    bgcolor: 'background.paper',
                }}
            >
                <IconButton
                    size="small"
                    onClick={() => navigate('/chats')}
                    sx={{ color: 'text.secondary' }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'text.primary',
                            lineHeight: 1.3,
                        }}
                    >
                        {conversation.conversationMeta?.title ||
                            'AI Conversation'}
                    </Typography>
                    {conversationId && (
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.disabled',
                                fontFamily: 'monospace',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {conversationId}
                        </Typography>
                    )}
                </Box>

                {(isLoading || systemTurnLoading || conversation.loading) && (
                    <CircularProgress
                        size={14}
                        sx={{ color: 'text.disabled' }}
                    />
                )}
            </Box>

            {/* Scrollable conversation area */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    px: { xs: 2, sm: 3, md: 4 },
                    py: 3,
                    '& > *': { maxWidth: 900, mx: 'auto' },
                }}
            >
                {systemTurnLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        <CircularProgress
                            size={12}
                            sx={{ color: 'text.disabled' }}
                        />
                        <Typography
                            sx={{ fontSize: '12px', color: 'text.disabled' }}
                        >
                            Loading context…
                        </Typography>
                    </Box>
                )}

                {/* Ghost user bubble while first response loads with no prior turns */}
                {allTurns.length === 0 && isLoading && initialQuery && (
                    <Box
                        sx={{
                            alignSelf: 'flex-end',
                            bgcolor: t => t.palette.action.hover,
                            borderRadius: '6px',
                            px: 1.5,
                            py: 1,
                            maxWidth: '85%',
                            mb: 2,
                        }}
                    >
                        <Typography
                            sx={{ fontSize: '13px', color: 'text.disabled' }}
                        >
                            {initialQuery}
                        </Typography>
                    </Box>
                )}

                {allTurns.length > 0 && <ConversationThread turns={allTurns} />}

                {allTurns.length > 0 && isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            pt: 1.5,
                            maxWidth: 900,
                            mx: 'auto',
                        }}
                    >
                        <CircularProgress
                            size={12}
                            sx={{ color: 'text.disabled' }}
                        />
                        <Typography
                            sx={{ fontSize: '12px', color: 'text.disabled' }}
                        >
                            Thinking…
                        </Typography>
                    </Box>
                )}

                <div ref={bottomRef} />
            </Box>

            {/* Sticky follow-up input — position sticky ensures it never scrolls out even if
                the parent layout doesn't fully constrain the flex container's height */}
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1,
                    flexShrink: 0,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Box
                    sx={{
                        maxWidth: 900,
                        mx: 'auto',
                        px: { xs: 2, sm: 3, md: 4 },
                        py: 1.5,
                    }}
                >
                    <AISearchField
                        mode="followup"
                        onSubmit={handleSubmit}
                        loading={isLoading}
                    />
                </Box>
            </Box>
        </Box>
    );
}
